import { memo, useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import type { PitchControlGrid } from '@4cm/core/tactics/overlays';

/**
 * 3D-native Pitch Control overlay. Mirrors `PitchControlLayer2D` but
 * renders one InstancedMesh of plane cells on the pitch (y ≈ 0.005,
 * just below the heatmap so density warmth reads over the ownership
 * tint when both are active).
 *
 * Per-instance colour interpolates home green ↔ neutral grey ↔ away
 * amber based on the cell's `homeShare ∈ [0, 1]`. Cells inside the
 * narrow neutral band (±NEUTRAL_SKIP_BAND around 0.5) are culled —
 * mirrors the 2D layer's optimisation since mid-grey conveys no
 * information.
 *
 * `showAway` (the master opponent toggle) hides away-leaning cells
 * (share < 0.5) so the coach can focus on home's controlled regions
 * without the opposition's tint distracting.
 */

const NEUTRAL_SKIP_BAND = 0.04;

const HOME_RGB = { r: 74 / 255, g: 222 / 255, b: 128 / 255 };
const AWAY_RGB = { r: 251 / 255, g: 191 / 255, b: 36 / 255 };
const NEUTRAL_RGB = { r: 160 / 255, g: 160 / 255, b: 160 / 255 };

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Two-segment lerp through grey at 0.5 — keeps the midline reading
 *  as "contested" instead of a muddied blend of green + amber. */
function colorFor(homeShare: number): [number, number, number] {
  if (homeShare >= 0.5) {
    const t = (homeShare - 0.5) * 2;
    return [
      lerp(NEUTRAL_RGB.r, HOME_RGB.r, t),
      lerp(NEUTRAL_RGB.g, HOME_RGB.g, t),
      lerp(NEUTRAL_RGB.b, HOME_RGB.b, t),
    ];
  }
  const t = (0.5 - homeShare) * 2;
  return [
    lerp(NEUTRAL_RGB.r, AWAY_RGB.r, t),
    lerp(NEUTRAL_RGB.g, AWAY_RGB.g, t),
    lerp(NEUTRAL_RGB.b, AWAY_RGB.b, t),
  ];
}

export interface PitchControlLayer3DProps {
  grid: PitchControlGrid | null;
  opacity: number;
  /** Hide away-leaning cells (share < 0.5) when false. Default true. */
  showAway?: boolean;
}

const _dummy = new THREE.Object3D();
const _color = new THREE.Color();

function PitchControlLayer3DInner({
  grid,
  opacity,
  showAway = true,
}: PitchControlLayer3DProps) {
  const cells = useMemo(() => {
    if (!grid || grid.homeShare.length === 0) return null;
    const { nRows, nCols, step, halfL, halfW, homeShare } = grid;
    const items: Array<{
      x: number;
      z: number;
      r: number;
      g: number;
      b: number;
    }> = [];
    for (let r = 0; r < nRows; r++) {
      const cx = -halfW + (r + 0.5) * step;
      for (let c = 0; c < nCols; c++) {
        const share = homeShare[r * nCols + c]!;
        if (share === 0) continue;
        if (Math.abs(share - 0.5) < NEUTRAL_SKIP_BAND) continue;
        if (!showAway && share < 0.5) continue;
        const cz = -halfL + (c + 0.5) * step;
        const [cr, cg, cb] = colorFor(share);
        items.push({ x: cx, z: cz, r: cr, g: cg, b: cb });
      }
    }
    return { items, step };
  }, [grid, showAway]);

  const geometry = useMemo(
    () => new THREE.PlaneGeometry(cells?.step ?? 1, cells?.step ?? 1),
    [cells?.step],
  );
  const material = useMemo(
    () =>
      new THREE.MeshBasicMaterial({
        vertexColors: true,
        transparent: true,
        opacity,
        depthWrite: false,
        side: THREE.DoubleSide,
      }),
    [opacity],
  );

  const meshRef = useRef<THREE.InstancedMesh>(null);

  useEffect(() => {
    const mesh = meshRef.current;
    if (!mesh || !cells) return;
    const { items } = cells;
    for (let i = 0; i < items.length; i++) {
      const item = items[i]!;
      _dummy.position.set(item.x, 0, item.z);
      _dummy.rotation.set(-Math.PI / 2, 0, 0);
      _dummy.scale.set(1, 1, 1);
      _dummy.updateMatrix();
      mesh.setMatrixAt(i, _dummy.matrix);
      _color.setRGB(item.r, item.g, item.b);
      mesh.setColorAt(i, _color);
    }
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  }, [cells]);

  if (!cells || cells.items.length === 0) return null;

  return (
    <instancedMesh
      ref={meshRef}
      args={[geometry, material, cells.items.length]}
      position={[0, 0.005, 0]}
      raycast={() => undefined}
      renderOrder={0}
    />
  );
}

export const PitchControlLayer3D = memo(PitchControlLayer3DInner);
